import { type ReactElement } from 'react';
import { r, s } from '@/theme/tokens';
import { Box, Skeleton, TableCell, TableRow } from '@/ui/primitives';
import { KeyboardArrowRightOutlinedIcon } from '@/ui/primitives/icons';
import { tableV2ChevronCellSx } from './TableV2ChevronCellStyles';
import {
  tableV2ChevronColumnWidth,
  tableV2ChevronIconSize,
  tableV2SkeletonChevronOffsetTransform,
} from './TableV2Constants';
import { resolveTableV2BodyRowHeight } from './TableV2Heights';

export type TableV2SkeletonColumnKind =
  | 'actions'
  | 'chevron'
  | 'dateTime'
  | 'duration'
  | 'empty'
  | 'metric'
  | 'name'
  | 'passRate'
  | 'project'
  | 'status'
  | 'tags';

const tableV2SkeletonWidths = ['72%', '58%', '78%', '64%', '70%', '54%'] as const;

const getTableV2SkeletonWidth = (rowIndex: number, offset = 0): string =>
  tableV2SkeletonWidths[(rowIndex + offset) % tableV2SkeletonWidths.length];

const tableV2SkeletonLineHeight = '17px';

const skeletonSx = {
  borderRadius: r('pill'),
  transform: 'none',
} as const;

const tableV2MetricSkeletonCellSx = {
  pl: s('none'),
  textAlign: 'left',
} as const;

const TableV2SkeletonChevron = (): ReactElement => (
  <Box
    component="span"
    sx={{
      alignItems: 'center',
      borderRadius: r('pill'),
      boxSizing: 'border-box',
      color: (theme) => theme.app.colors.textMuted,
      display: 'inline-flex',
      flex: '0 0 auto',
      height: tableV2ChevronColumnWidth,
      justifyContent: 'center',
      left: '50%',
      lineHeight: 0,
      p: s('none'),
      position: 'absolute',
      textAlign: 'center',
      top: '50%',
      transform: tableV2SkeletonChevronOffsetTransform,
      verticalAlign: 'middle',
      width: tableV2ChevronColumnWidth,
      '& .MuiSvgIcon-root': {
        fontSize: tableV2ChevronIconSize,
        height: tableV2ChevronIconSize,
        width: tableV2ChevronIconSize,
      },
    }}
  >
    <KeyboardArrowRightOutlinedIcon
      sx={{
        display: 'block',
        opacity: 0.55,
      }}
    />
  </Box>
);

const TableV2SkeletonActions = (): ReactElement => (
  <Box sx={{ alignItems: 'center', display: 'grid', gap: '3px', justifyContent: 'center' }}>
    {Array.from({ length: 3 }, (_item, index) => (
      <Box
        key={`table-v2-action-dot-${String(index)}`}
        sx={{
          backgroundColor: (theme) => theme.app.colors.textMuted,
          borderRadius: r('pill'),
          height: '3px',
          opacity: 0.55,
          width: '3px',
        }}
      />
    ))}
  </Box>
);

const TableV2SkeletonStatus = (): ReactElement => (
  <Skeleton height="28px" sx={skeletonSx} variant="rounded" width="72px" />
);

export const TableV2SkeletonCell = ({
  kind,
  rowIndex,
}: {
  kind: TableV2SkeletonColumnKind;
  rowIndex: number;
}): ReactElement => {
  switch (kind) {
    case 'actions':
      return <TableV2SkeletonActions />;
    case 'chevron':
      return <TableV2SkeletonChevron />;
    case 'dateTime':
      return (
        <Box sx={{ display: 'grid', gap: s('md') }}>
          <Skeleton
            height={tableV2SkeletonLineHeight}
            sx={skeletonSx}
            variant="rounded"
            width={getTableV2SkeletonWidth(rowIndex)}
          />
          <Skeleton
            height={tableV2SkeletonLineHeight}
            sx={skeletonSx}
            variant="rounded"
            width={getTableV2SkeletonWidth(rowIndex, 2)}
          />
        </Box>
      );
    case 'duration':
      return (
        <Skeleton
          height={tableV2SkeletonLineHeight}
          sx={skeletonSx}
          variant="rounded"
          width={getTableV2SkeletonWidth(rowIndex, 3)}
        />
      );
    case 'empty':
      return <Box aria-hidden="true" />;
    case 'metric':
      return (
        <Skeleton
          height={tableV2SkeletonLineHeight}
          sx={skeletonSx}
          variant="rounded"
          width={getTableV2SkeletonWidth(rowIndex, 3)}
        />
      );
    case 'name':
      return (
        <Box sx={{ display: 'grid', gap: s('md'), minWidth: 0 }}>
          <Skeleton
            height={tableV2SkeletonLineHeight}
            sx={skeletonSx}
            variant="rounded"
            width={getTableV2SkeletonWidth(rowIndex)}
          />
          <Skeleton
            height={tableV2SkeletonLineHeight}
            sx={skeletonSx}
            variant="rounded"
            width={getTableV2SkeletonWidth(rowIndex, 1)}
          />
        </Box>
      );
    case 'passRate':
      return (
        <Box sx={{ display: 'grid', gap: s('md'), minWidth: s('3xl') }}>
          <Skeleton
            height={tableV2SkeletonLineHeight}
            sx={skeletonSx}
            variant="rounded"
            width={getTableV2SkeletonWidth(rowIndex, 2)}
          />
          <Skeleton height="12px" sx={skeletonSx} variant="rounded" width="100%" />
        </Box>
      );
    case 'project':
      return (
        <Box sx={{ alignItems: 'center', display: 'flex', minWidth: 0 }}>
          <Skeleton
            height={tableV2SkeletonLineHeight}
            sx={skeletonSx}
            variant="rounded"
            width={getTableV2SkeletonWidth(rowIndex, 1)}
          />
        </Box>
      );
    case 'status':
      return <TableV2SkeletonStatus />;
    case 'tags':
      return (
        <Box sx={{ columnGap: s('sm'), display: 'flex', flexWrap: 'wrap', rowGap: s('md') }}>
          <Skeleton
            height={tableV2SkeletonLineHeight}
            sx={skeletonSx}
            variant="rounded"
            width="72px"
          />
        </Box>
      );
    default: {
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
};

export const TableV2SkeletonRow = ({
  columns,
  isLastVisibleRow,
  rowIndex,
}: {
  columns: TableV2SkeletonColumnKind[];
  isLastVisibleRow: boolean;
  rowIndex: number;
}): ReactElement => (
  <TableRow
    sx={{
      backgroundColor: 'background.paper',
      '& > td': {
        borderBottom: isLastVisibleRow ? 0 : (theme) => `1px solid ${theme.app.colors.border}`,
        height: resolveTableV2BodyRowHeight(!isLastVisibleRow),
        maxHeight: resolveTableV2BodyRowHeight(!isLastVisibleRow),
        py: s('md'),
        verticalAlign: 'middle',
      },
    }}
  >
    {columns.map((kind, columnIndex) => (
      <TableCell
        key={`${kind}-${String(columnIndex)}`}
        align={kind === 'actions' || kind === 'chevron' ? 'center' : undefined}
        sx={
          kind === 'chevron'
            ? tableV2ChevronCellSx
            : kind === 'metric'
              ? tableV2MetricSkeletonCellSx
              : undefined
        }
      >
        <TableV2SkeletonCell kind={kind} rowIndex={rowIndex} />
      </TableCell>
    ))}
  </TableRow>
);
